import React, { useEffect, useMemo, useState } from 'react';
import { avatarColorForName } from '../../utils/avatarColor';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import {
  PageHeader, Modal, Badge, EmptyState, useToast, InlineSpinner, TableSkeletonRows,
  usePagination, Pagination, useSort, SortTh, FilterBar, FilterSelect,
  claimStatusBadgeMap,
} from '../../components/UI';

function fmtMoney(v) {
  const n = Number(v);
  return isNaN(n) ? '—' : `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtHours(h) {
  if (h === null || h === undefined || h === '') return '—';
  const n = Number(h);
  return isNaN(n) ? '—' : n.toFixed(1);
}

function claimAmountLabel(claim) {
  const amount = Number(claim?.amount);
  if (!Number.isFinite(amount) || amount <= 0) return 'Pending Amount';
  return fmtMoney(claim.amount);
}

// csv-escape: wrap in quotes and double up any embedded quotes whenever the
// value could otherwise break the column boundary (comma/quote/newline).
function csvCell(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const STATUS_FILTERS = [
  { value: 'approved', label: 'Awaiting payment' },
  { value: 'paid',     label: 'Paid' },
];

const EMPTY_COPY = {
  approved: { icon: 'bi-check2-circle', message: 'Nothing waiting on payment right now' },
  paid:     { icon: 'bi-cash-coin',      message: 'No payments released yet' },
};

// Deterministic per-name hue so a trainer's avatar chip stays the same color
// across renders/filters instead of picking a new random one every time.
function nameColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return avatarColorForName(name);
}
function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

function Avatar({ name }) {
  return (
    <span style={{
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      background: nameColor(name), color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, letterSpacing: .2,
    }}>
      {initials(name)}
    </span>
  );
}

function FinanceStatCard({ label, value, icon, color, sub, active, onClick, alert }) {
  return (
    <div className={`sms-stat-card sms-claim-stat-card h-100${onClick ? ' sms-stat-card-clickable' : ''}`}
      role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }) : undefined}
      style={{
        position: 'relative', overflow: 'hidden',
        background: `linear-gradient(180deg, color-mix(in srgb, ${color} 7%, var(--sms-sur)), var(--sms-sur) 65%)`,
        borderColor: active ? color : undefined,
        boxShadow: active ? `0 0 0 1px ${color} inset` : undefined,
      }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
        <div className="sms-claim-stat-icon" style={{ position: 'relative', width: 34, height: 34, borderRadius: 9, background: `color-mix(in srgb, ${color} 14%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`bi ${icon}`} style={{ color, fontSize: 16 }} />
          {alert && (
            <span aria-hidden="true" style={{
              position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: '50%',
              background: 'var(--sms-stat-red)', boxShadow: '0 0 0 2px var(--sms-sur)',
            }}>
              <span style={{
                position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--sms-stat-red)',
                animation: 'sms-ping 1.6s cubic-bezier(0,0,0.2,1) infinite',
              }} />
            </span>
          )}
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-12' : 'col-6'}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--sms-txt3)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--sms-txt)' }}>{children}</div>
    </div>
  );
}

export default function FinanceClaimsPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState('approved');
  const [search, setSearch] = useState('');

  const [reviewTarget, setReviewTarget] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  const [payTarget, setPayTarget] = useState(null);
  const [payRef, setPayRef] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  const [reportOpen, setReportOpen] = useState(false);
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [reportFrom, setReportFrom] = useState(monthStart);
  const [reportTo, setReportTo] = useState(today.toISOString().slice(0, 10));

  const load = () => {
    setLoading(true);
    api.get('/finance/claims')
      .then(r => setClaims(r.data.data || []))
      .catch(() => toast('Failed to load claims', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    const approved = claims.filter(c => c.status === 'approved');
    const paid = claims.filter(c => c.status === 'paid');
    const now = new Date();
    const paidThisMonth = paid.filter(c => {
      if (!c.paid_at) return false;
      const d = new Date(c.paid_at);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    return {
      approvedCount: approved.length,
      approvedAmount: approved.reduce((sum, c) => sum + Number(c.amount), 0),
      paidCount: paid.length,
      paidThisMonthAmount: paidThisMonth.reduce((sum, c) => sum + Number(c.amount), 0),
      paidThisMonthCount: paidThisMonth.length,
    };
  }, [claims]);

  const q = search.trim().toLowerCase();
  const filtered = claims.filter(c => c.status === filter && (!q || c.trainer_name.toLowerCase().includes(q)));
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);

  const canPay = can('finance_claims', 'update');

  const openReview = async (claim) => {
    setReviewTarget({ ...claim, loadingDetail: true });
    try {
      const r = await api.get(`/finance/claims/${claim.id}`);
      setReviewTarget(r.data.data);
    } catch {
      toast('Failed to load claim detail', 'error');
      setReviewTarget(null);
    }
  };

  const printClaim = async (id) => {
    setPrintingId(id);
    try {
      const res = await api.get(`/finance/claims/${id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      toast('Failed to generate PDF', 'error');
    } finally {
      setPrintingId(null);
    }
  };

  const openPay = (claim) => { setPayTarget(claim); setPayRef(''); setPayNotes(''); setPayError(''); };

  const confirmPay = async () => {
    if (!payRef.trim()) { setPayError('Enter a payment reference number'); return; }
    setPaying(true);
    try {
      await api.put(`/finance/claims/${payTarget.id}/pay`, { payment_ref: payRef.trim(), payment_notes: payNotes.trim() || null });
      toast(`Marked ${payTarget.trainer_name}'s claim as paid`, 'success');
      setClaims(prev => prev.map(c => c.id === payTarget.id
        ? { ...c, status: 'paid', payment_ref: payRef.trim(), payment_notes: payNotes.trim() || null, paid_at: new Date().toISOString(), paid_by_name: user?.full_name }
        : c));
      setReviewTarget(null);
      setPayTarget(null);
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to release payment', 'error');
    } finally {
      setPaying(false);
    }
  };

  const reportRows = useMemo(() => {
    if (!reportFrom || !reportTo) return [];
    return claims.filter(c => {
      const ref = (c.paid_at || c.decided_at || c.claim_date || '').slice(0, 10);
      return ref >= reportFrom && ref <= reportTo;
    });
  }, [claims, reportFrom, reportTo]);

  const reportTotals = useMemo(() => ({
    hours: reportRows.reduce((sum, c) => sum + Number(c.hours), 0),
    amount: reportRows.reduce((sum, c) => sum + Number(c.amount), 0),
  }), [reportRows]);

  const exportReportCsv = () => {
    const header = ['Trainer', 'Module', 'Hours', 'Amount (S$)', 'Status', 'Approved By', 'Approved On', 'Payment Ref', 'Paid On'];
    const lines = reportRows.map(c => [
      c.trainer_name, c.module_name || c.module_code || '', fmtHours(c.hours), Number(c.amount).toFixed(2),
      claimStatusBadgeMap[c.status]?.label || c.status, c.approved_by_name || '', fmtDate(c.decided_at),
      c.payment_ref || '', fmtDate(c.paid_at),
    ]);
    const csv = [header, ...lines].map(row => row.map(csvCell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `finance-claims-report_${reportFrom}_to_${reportTo}.csv`;
    a.click();
    toast('Report exported', 'success');
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = (user?.full_name || 'there').split(' ')[0];
  const empty = EMPTY_COPY[filter] || { icon: 'bi-receipt', message: 'No claims in this view' };

  return (
    <div className="trn-page">
      <div className="sms-card mb-3" style={{
        padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
        background: 'linear-gradient(135deg, color-mix(in srgb, var(--sms-stat-violet) 9%, var(--sms-sur)), var(--sms-sur) 60%)',
      }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--sms-txt)' }}>{greeting}, {firstName}</div>
          <div style={{ fontSize: 13, color: 'var(--sms-txt2)', marginTop: 2 }}>
            {summary.approvedCount
              ? <>You have <b style={{ color: 'var(--sms-txt)' }}>{summary.approvedCount} claim{summary.approvedCount === 1 ? '' : 's'}</b> approved by admin, totalling <b style={{ color: 'var(--sms-txt)' }}>{fmtMoney(summary.approvedAmount)}</b> awaiting release.</>
              : 'Nothing is waiting on payment right now — all caught up.'}
          </div>
        </div>
        <div style={{
          width: 48, height: 48, borderRadius: 14, flexShrink: 0,
          background: 'color-mix(in srgb, var(--sms-stat-violet) 16%, transparent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className="bi bi-wallet2" style={{ color: 'var(--sms-stat-violet)', fontSize: 22 }} />
        </div>
      </div>

      {summary.approvedCount > 0 && (
        <div className="d-flex align-items-center gap-2" style={{
          background: 'color-mix(in srgb, var(--sms-stat-red) 8%, var(--sms-sur2))',
          border: '1px solid color-mix(in srgb, var(--sms-stat-red) 25%, var(--sms-bdr))',
          borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: 'var(--sms-txt)',
        }}>
          <span style={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--sms-stat-red)' }} />
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--sms-stat-red)', animation: 'sms-ping 1.6s cubic-bezier(0,0,0.2,1) infinite' }} />
          </span>
          <span>
            <b>{summary.approvedCount} claim{summary.approvedCount === 1 ? '' : 's'}</b> waiting on payment
            {' — '}{fmtMoney(summary.approvedAmount)} to release.
          </span>
        </div>
      )}

      <PageHeader title="Claims for Payment" subtitle="Review admin-approved trainer claims and release payment"
        actions={
          <button className="sms-btn sms-btn-outline-info sms-claim-report-btn" onClick={() => setReportOpen(true)}>
            <i className="bi bi-file-earmark-bar-graph" /> Generate report
          </button>
        } />

      <div className="row g-3 mb-3 sms-stat-anim-row">
        <div className="col-md-4">
          <FinanceStatCard label="Awaiting Payment" value={summary.approvedCount} icon="bi-hourglass-split" color="var(--sms-stat-red)"
            sub={summary.approvedCount ? `${fmtMoney(summary.approvedAmount)} approved by admin` : 'Nothing awaiting payment'}
            active={filter === 'approved'} onClick={() => setFilter('approved')} alert={summary.approvedCount > 0} />
        </div>
        <div className="col-md-4">
          <FinanceStatCard label="Paid" value={fmtMoney(summary.paidThisMonthAmount)} icon="bi-cash-coin" color="var(--sms-stat-green)"
            sub={`${summary.paidThisMonthCount} claim${summary.paidThisMonthCount === 1 ? '' : 's'} released this month`}
            active={filter === 'paid'} onClick={() => setFilter('paid')} />
        </div>
        <div className="col-md-4">
          <FinanceStatCard label="Total Released" value={summary.paidCount} icon="bi-check2-circle" color="var(--sms-stat-blue)"
            sub="All-time paid claims" />
        </div>
      </div>

      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search trainer name…">
        <FilterSelect value={filter} onChange={setFilter} placeholder="Status" options={STATUS_FILTERS} />
      </FilterBar>

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon={empty.icon} message={empty.message} /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table sms-claim-table">
              <thead><tr>
                <SortTh label="Trainer" field="trainer_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Module / Intake</th>
                <SortTh label="Hours" field="hours" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} style={{ textAlign: 'right' }} />
                <SortTh label="Amount" field="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} style={{ textAlign: 'right' }} />
                <th>Approved By</th>
                <SortTh label="Approved On" field="decided_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Status</th>
                <th style={{ width: 190 }}>Action</th>
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={8} /> : paged.map(c => (
                  <tr key={c.id} className="sms-claim-row">
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Avatar name={c.trainer_name} />
                        <span style={{ fontWeight: 500 }}>{c.trainer_name}</span>
                      </div>
                    </td>
                    <td>
                      {c.session_count > 1
                        ? `${c.session_count} sessions${c.module_name ? ` (${c.module_name})` : ''}`
                        : (c.module_name || c.module_code || '—')}
                      {c.intake_code ? ` · ${c.intake_code}` : ''}
                    </td>
                    <td className="num">{fmtHours(c.hours)}</td>
                    <td className="num" style={{ fontWeight: 700, color: 'var(--sms-txt)' }}>{claimAmountLabel(c)}</td>
                    <td>{c.approved_by_name || '—'}</td>
                    <td>{fmtDate(c.decided_at)}</td>
                    <td><Badge value={c.status} map={claimStatusBadgeMap} /></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="sms-btn sms-btn-outline" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => openReview(c)}>
                          <i className="bi bi-eye" /> Review
                        </button>
                        {c.status === 'approved' && canPay && (
                          <button className="sms-btn sms-btn-outline-info" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => openPay(c)}>
                            <i className="bi bi-cash-coin" /> Pay
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && (
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
              setPage={setPage} setPageSize={setPageSize} start={start} />
          )}
          </>
        )}
      </div>

      {/* Session-level review */}
      <Modal show={!!reviewTarget} onClose={() => setReviewTarget(null)} title="Claim Review">
        {reviewTarget && (
          <>
            <div className="d-flex align-items-center gap-3 mb-3" style={{
              padding: '14px 16px', borderRadius: 14,
              background: 'color-mix(in srgb, var(--sms-stat-violet) 8%, var(--sms-sur2))',
              border: '1px solid color-mix(in srgb, var(--sms-stat-violet) 20%, var(--sms-bdr))',
            }}>
              <Avatar name={reviewTarget.trainer_name} />
              <div className="flex-fill">
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sms-txt)' }}>{reviewTarget.trainer_name}</div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                  {reviewTarget.session_count > 1 ? `${reviewTarget.session_count} sessions` : (reviewTarget.module_name || reviewTarget.module_code || '—')}
                </div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--sms-stat-violet)', whiteSpace: 'nowrap' }}>{claimAmountLabel(reviewTarget)}</div>
            </div>

            {reviewTarget.loadingDetail ? (
              <div className="d-flex justify-content-center py-4"><InlineSpinner /></div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <Field label="Total Hours">{fmtHours(reviewTarget.hours)} hrs</Field>
                  <Field label="Status"><Badge value={reviewTarget.status} map={claimStatusBadgeMap} /></Field>
                  <Field label="Approved By">{reviewTarget.approved_by_name || '—'}</Field>
                  <Field label="Approved On">{fmtDate(reviewTarget.decided_at)}</Field>
                  {reviewTarget.status === 'paid' && (
                    <>
                      <Field label="Paid By">{reviewTarget.paid_by_name || '—'}</Field>
                      <Field label="Paid On">{fmtDate(reviewTarget.paid_at)}</Field>
                      <Field label="Payment Ref">{reviewTarget.payment_ref || '—'}</Field>
                      {reviewTarget.payment_notes && <Field label="Payment Notes" full>{reviewTarget.payment_notes}</Field>}
                    </>
                  )}
                </div>

                <hr style={{ borderColor: 'var(--sms-bdr)' }} />

                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--sms-txt3)', margin: '10px 0 8px' }}>
                  Sessions claimed ({reviewTarget.items?.length || 0})
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid var(--sms-bdr)', borderRadius: 10 }}>
                  <table className="sms-table" style={{ margin: 0 }}>
                    <thead><tr>
                      <th>Date</th><th>Module</th><th className="num">Hours</th><th className="num">Rate/hr</th><th className="num">Amount</th>
                    </tr></thead>
                    <tbody>
                      {(reviewTarget.items || []).map(it => (
                        <tr key={it.id}>
                          <td>{fmtDate(it.session_date)}</td>
                          <td>{it.module_name || it.module_code}</td>
                          <td className="num">{fmtHours(it.hours)}</td>
                          <td className="num">{fmtMoney(it.rate)}</td>
                          <td className="num" style={{ fontWeight: 600 }}>{fmtMoney(it.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex gap-2 justify-content-end mt-3">
                  <button className="sms-btn sms-btn-outline-info" disabled={printingId === reviewTarget.id} onClick={() => printClaim(reviewTarget.id)}>
                    {printingId === reviewTarget.id ? <InlineSpinner /> : <><i className="bi bi-printer" /> Print</>}
                  </button>
                  {reviewTarget.status === 'approved' && canPay && (
                    <button className="sms-btn sms-btn-success" onClick={() => openPay(reviewTarget)}>
                      <i className="bi bi-check2-circle" /> Release payment
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* Release payment */}
      <Modal show={!!payTarget} onClose={() => setPayTarget(null)} title="Release Payment">
        {payTarget && (
          <>
            <div className="d-flex align-items-center gap-3 mb-3" style={{
              padding: '14px 16px', borderRadius: 14,
              background: 'color-mix(in srgb, var(--sms-stat-green) 8%, var(--sms-sur2))',
              border: '1px solid color-mix(in srgb, var(--sms-stat-green) 20%, var(--sms-bdr))',
            }}>
              <Avatar name={payTarget.trainer_name} />
              <div className="flex-fill">
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sms-txt)' }}>{payTarget.trainer_name}</div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{payTarget.module_name || payTarget.module_code || `${payTarget.session_count} sessions`}</div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--sms-stat-green)', whiteSpace: 'nowrap' }}>{claimAmountLabel(payTarget)}</div>
            </div>

            <div className="row g-3 mb-3">
              <Field label="Hours Claimed">{fmtHours(payTarget.hours)} hrs</Field>
              <Field label="Approved By">{payTarget.approved_by_name || '—'}</Field>
              <Field label="Approved On">{fmtDate(payTarget.decided_at)}</Field>
            </div>

            <hr style={{ borderColor: 'var(--sms-bdr)' }} />

            <div className="mb-3">
              <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Payment Reference No.</label>
              <input className="sms-form-control" placeholder="e.g. PAYNOW-08234" value={payRef}
                onChange={e => { setPayRef(e.target.value); if (payError) setPayError(''); }} />
              {payError && <div style={{ fontSize: 12, color: 'var(--sms-stat-red)', marginTop: 4 }}>{payError}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>Finance Remarks (optional)</label>
              <input className="sms-form-control" placeholder="Notes for this payment…" value={payNotes} onChange={e => setPayNotes(e.target.value)} />
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="sms-btn sms-btn-outline flex-fill" onClick={() => setPayTarget(null)} disabled={paying}>Cancel</button>
              <button type="button" className="sms-btn sms-btn-success flex-fill" onClick={confirmPay} disabled={paying}>
                {paying ? <InlineSpinner /> : <><i className="bi bi-check2-circle" /> Mark as Paid</>}
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Payment report */}
      <Modal show={reportOpen} onClose={() => setReportOpen(false)} title="Payment Report">
        <div className="row g-3 mb-3">
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>From</label>
            <input type="date" className="sms-form-control" value={reportFrom} onChange={e => setReportFrom(e.target.value)} />
          </div>
          <div className="col-6">
            <label className="form-label" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)' }}>To</label>
            <input type="date" className="sms-form-control" value={reportTo} onChange={e => setReportTo(e.target.value)} />
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginBottom: 12 }}>
          Admin-approved claims decided or paid in this range. Rejected and trainer-cancelled claims are never included.
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6">
            <div style={{ background: 'var(--sms-sur2)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Total hours</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--sms-txt)' }}>{fmtHours(reportTotals.hours)}</div>
            </div>
          </div>
          <div className="col-6">
            <div style={{ background: 'var(--sms-sur2)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Total amount</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--sms-txt)' }}>{fmtMoney(reportTotals.amount)}</div>
            </div>
          </div>
        </div>

        {reportRows.length === 0 ? (
          <EmptyState icon="bi-file-earmark-bar-graph" message="No claims in this date range" />
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--sms-bdr)', borderRadius: 10, maxHeight: 260, overflowY: 'auto' }}>
            <table className="sms-table" style={{ margin: 0 }}>
              <thead><tr><th>Trainer</th><th className="num">Hours</th><th className="num">Amount</th><th>Status</th></tr></thead>
              <tbody>
                {reportRows.map(c => (
                  <tr key={c.id}>
                    <td>{c.trainer_name}</td>
                    <td className="num">{fmtHours(c.hours)}</td>
                    <td className="num" style={{ fontWeight: 600 }}>{claimAmountLabel(c)}</td>
                    <td><Badge value={c.status} map={claimStatusBadgeMap} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="sms-btn sms-btn-outline" onClick={() => setReportOpen(false)}>Close</button>
          <button className="sms-btn sms-btn-primary" disabled={!reportRows.length} onClick={exportReportCsv}>
            <i className="bi bi-download" /> Export CSV
          </button>
        </div>
      </Modal>
    </div>
  );
}
